import matchErrorsToFieldNames from "../FormBaseComponent/matchErrorsToFieldNames";
import * as React from "react";
import BaseComponent from "../BaseComponent";
import {OctopusError} from "client/resources";
import moment = require("moment");

export interface Errors {
    message: string;
    details: string[];
    detailLinks?: string[];
    helpText?: string;
    helpLink?: string;
    fullException?: string;
    fieldErrors: {
        [other: string]: string;
    };
}

export interface DataBaseComponentState {
    busy?: Promise<void>;
    errors?: Errors;
}

export type DoBusyTask = (action: () => Promise<any>, clearCurrentErrors?: boolean) => Promise<boolean>;

export class DataBaseComponent<Props, State extends DataBaseComponentState> extends BaseComponent<Props, State> {
    private refreshId = 0;
    private busies: Array<Promise<void>> = [];
    constructor(props: Props) {
        super(props);

        this.provideErrorHandling(this.doBusyTaskInternal);
    }

    public doBusyTask: DoBusyTask = (action: () => Promise<any>, clearCurrentErrors: boolean = true): Promise<boolean> => {
        return this.doBusyTaskInternal(action, clearCurrentErrors);
    }

    protected async doRefresh<K extends keyof State>(getData: () => Promise<Pick<State, K>>) {
        return this.doBusyTask(async () => {
            const get = getData();
            this.refreshId += 1;
            const currentRefreshId = this.refreshId;
            const result = await get;
            if (currentRefreshId === this.refreshId) {
                this.setState(result);
            }
        });
    }

    protected async beginRefreshLoop<K extends keyof State>(getData: () => Promise<Pick<State, K>>, refreshInterval: number, lastRefresh?: moment.Moment) {
        if (this.unmounted) {
            return;
        }

        try {
            const interval = document.hidden ? refreshInterval * 12 : refreshInterval;

            if (!lastRefresh || moment().diff(lastRefresh, "ms") >= interval) {
                await this.doRefresh(getData);
                lastRefresh = moment();
            }

        } finally {
            window.setTimeout(() => this.beginRefreshLoop(getData, refreshInterval, lastRefresh), 250);
        }
    }

    protected setError = (message: string, details: string[] = [], fieldErrors: { [other: string]: string; }= {}) => {
        this.setState({errors: {
            message,
            details,
            fieldErrors
        }});
    }

    protected mapToOctopusError(err: OctopusError) {
        // we override this in subclasses so don't remove
        return createErrorsFromOctopusError(err);
    }

    private doBusyTaskInternal = async (action: () => Promise<void>, clearCurrentErrors: boolean): Promise<boolean> => {
        let busy: Promise<void>;
        try {
            // Sometimes child components will load some lookup data while a parent component
            // is displaying an error. The child uses the parent's doBusyTask so that the busy
            // indicator and errors display correctly. But we shouldn't clear existing errors
            // from that child load.
            if (clearCurrentErrors) {
                this.setState({
                    errors: null
                });
            }

            busy = action();
            this.busies = [busy, ...this.busies];
            const singlePromise = Promise.all(this.busies).then(v => {/* */}); //the .then gives us Promise<void> instead of Promise<void[]>
            this.setState({busy: singlePromise});
            await busy;

            return true;

        } catch (e) {
            if (e instanceof OctopusError) {
                const errors = this.mapToOctopusError(e);
                this.setState({errors});
                return false;
            }
            throw e;
        } finally {
            this.busies = this.busies.filter(b => b !== busy);
            // we need to return null here when done
            // because some buttons etc just check for the
            // existance of a busy promise
            this.setState({busy: this.busies.length > 0 ? Promise.all(this.busies).then(v => {/* */}) : null});
        }
    }
}

export function createErrorsFromOctopusError(err: OctopusError): Errors {
    return {
        message: err.ErrorMessage,
        details: err.Errors || [],
        detailLinks: err.ParsedHelpLinks,
        helpLink: err.HelpLink,
        helpText: err.HelpText,
        fullException: err.FullException,
        fieldErrors: {}
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/DataBaseComponent/DataBaseComponent.tsx